import { Campaign } from '@/core/api/entities/campaign/campaign'
import { RRuleService } from '@/core/utils/rruleService'

import { type CampaignConfigFormValues } from '../schemas/createCampaignSchema'

type DayCode = CampaignConfigFormValues['selectedDays'][number]

interface ParsedRRule {
	freq: string | null
	interval: number
	selectedDays: DayCode[]
	timeFrom: string
	timeTo: string
}

const parseRRule = (
	rrule: string | null | undefined,
	rruleType?: string | null
): ParsedRRule => {
	if (!rrule) {
		return {
			freq: null,
			interval: 30,
			selectedDays: [],
			timeFrom: '11:00',
			timeTo: '23:00',
		}
	}

	const parts = rrule.split(';')
	const map: Record<string, string> = {}

	for (const part of parts) {
		const [key, value] = part.split('=')
		if (key && value) {
			map[key.toUpperCase()] = value
		}
	}

	const freq = map.FREQ || null
	const interval = map.INTERVAL ? parseInt(map.INTERVAL, 10) || 30 : 30

	const selectedDays: DayCode[] = map.BYDAY
		? (map.BYDAY.split(',').filter(Boolean) as DayCode[])
		: []

	const byHourValues = map.BYHOUR
		? map.BYHOUR.split(',')
				.map((h) => parseInt(h, 10))
				.filter((h) => !Number.isNaN(h))
		: []

	// ONE_PER_DAY / ONE_TIME_ONLY: single exact time, use BYHOUR + BYMINUTE
	if (rruleType === 'one_per_day' || rruleType === 'one_time_only') {
		const hour = byHourValues.length > 0 ? byHourValues[0] : 12
		const minuteStr = map.BYMINUTE ?? '0'
		const minute = Number.isNaN(parseInt(minuteStr, 10))
			? 0
			: Math.max(0, Math.min(59, parseInt(minuteStr, 10)))

		const hh = String(hour).padStart(2, '0')
		const mm = String(minute).padStart(2, '0')

		return {
			freq,
			interval,
			selectedDays,
			timeFrom: `${hh}:${mm}`,
			// For once-a-day we only care about a single time, "To" is unused
			timeTo: `${hh}:${mm}`,
		}
	}

	// RECURRENT / others: hour window with exclusive upper bound
	const timeFrom =
		byHourValues.length > 0
			? `${String(byHourValues[0]).padStart(2, '0')}:00`
			: '11:00'

	// Interpret BYHOUR as an inclusive list of hours where playback can start.
	// The UI "To" field represents the exclusive upper bound, so we add 1 hour
	// to the last BYHOUR value when mapping back to the form.
	let timeTo = '23:00'
	if (byHourValues.length > 0) {
		const lastHour = byHourValues[byHourValues.length - 1]
		const toHour = Math.min(lastHour + 1, 23)
		timeTo = `${String(toHour).padStart(2, '0')}:00`
	}

	return {
		freq,
		interval,
		selectedDays,
		timeFrom,
		timeTo,
	}
}

export const mapCampaignToConfigFormValues = (
	campaign: Campaign
): CampaignConfigFormValues => {
	const parsed = parseRRule(campaign.rrule, campaign.rruleType)

	const { dtstart, until } = RRuleService.getRecurrenceDates(campaign.rrule)

	const rruleType: CampaignConfigFormValues['rruleType'] =
		campaign.rruleType === 'recurrent' ||
		campaign.rruleType === 'one_per_day' ||
		campaign.rruleType === 'one_time_only'
			? campaign.rruleType
			: 'recurrent'

	const playEveryUnit: CampaignConfigFormValues['playEveryUnit'] =
		parsed.freq === 'HOURLY' ? 'hours' : 'minutes'

	// If the RRULE does not specify explicit boundaries via DTSTART/UNTIL,
	// we keep the form dates empty instead of defaulting to "today → +7 days".
	// This way "no date configured" in the backend is represented as
	// "no date selected" in the UI.
	const startDate = dtstart ? dtstart.toJSDate() : undefined
	const endDate = until ? until.toJSDate() : undefined

	const type: CampaignConfigFormValues['type'] =
		campaign.type === 'opening' ||
		campaign.type === 'closing' ||
		campaign.type === 'other'
			? campaign.type
			: 'other'

	return {
		name: campaign.name,
		rruleType,
		startDate,
		endDate,
		playEvery: parsed.interval,
		playEveryUnit,
		selectedDays: parsed.selectedDays,
		timeFrom: parsed.timeFrom,
		timeTo: parsed.timeTo,
		exdate: campaign.exdate || [],
		priority: campaign.priority,
		interruptsMusic: campaign.type === 'opening',
		type,
	}
}
